/**
 * ParentalDashboard -- Dashboard for parents to monitor and control child profiles.
 *
 * Accessible from Settings > "Parental Controls" (only shown for parent profiles).
 * Provides activity monitoring, time controls, content controls, approval queue,
 * PIN management, and independence transition.
 */

import type { AweniContext } from '../../context/AweniContext';
import {
  ParentalControlService,
  getParentalControlService,
  initParentalControlService,
} from '../../services/ParentalControlService';
import type { ParentalControls, ChildActivity } from '../../services/ParentalControlService';

// ---------- Types ----------

interface ChildProfile {
  id: number;
  name: string;
  avatar_url?: string;
}

interface ApprovalItem {
  id: string;
  childName: string;
  action: string;
  detail: string;
  timestamp: string;
}

// ---------- Constants ----------

const TIME_LIMIT_OPTIONS = [
  { label: '30 min', value: 30 },
  { label: '1 h', value: 60 },
  { label: '1.5 h', value: 90 },
  { label: '2 h', value: 120 },
  { label: '3 h', value: 180 },
  { label: '', value: 0 }, // "Unlimited" -- label set via i18n
];

const DIFFICULTY_OPTIONS: Array<{ key: string; value: ParentalControls['difficulty_cap'] }> = [
  { key: 'easy', value: 'easy' },
  { key: 'medium', value: 'medium' },
  { key: 'hard', value: 'hard' },
  { key: 'expert', value: 'expert' },
];

const MODULE_IDS = ['games', 'memoriz', 'balancetab', 'selecto', 'blog', 'chat', 'community'] as const;

// ---------- CSS ----------

const DASHBOARD_CSS = `
.pc-dashboard { max-width: 640px; margin: 0 auto; padding: 0 16px 120px; }
.pc-dashboard h1 { font-size: 1.5rem; font-weight: 700; margin: 8px 0 4px; color: var(--text-primary); }
.pc-dashboard .pc-subtitle { font-size: 0.875rem; color: var(--text-secondary); margin-bottom: 20px; }

/* Child Tabs */
.pc-tabs { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 12px; margin-bottom: 16px; scrollbar-width: none; }
.pc-tabs::-webkit-scrollbar { display: none; }
.pc-tab { display: flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 12px; border: 2px solid var(--border); background: var(--card-bg); cursor: pointer; white-space: nowrap; transition: all 150ms ease; flex-shrink: 0; }
.pc-tab:hover { border-color: var(--accent); }
.pc-tab.active { border-color: var(--accent); background: rgba(16, 185, 129, 0.1); }
.pc-tab-avatar { width: 28px; height: 28px; border-radius: 50%; background: var(--bg-secondary); display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 600; color: var(--text-secondary); overflow: hidden; }
.pc-tab-avatar img { width: 100%; height: 100%; object-fit: cover; }
.pc-tab-name { font-size: 0.875rem; font-weight: 600; color: var(--text-primary); }

/* Section Card */
.pc-card { background: var(--card-bg); border-radius: 16px; padding: 20px; margin-bottom: 12px; box-shadow: var(--shadow-sm); }
.pc-card-title { font-size: 1rem; font-weight: 600; color: var(--text-primary); margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
.pc-card-title svg { width: 20px; height: 20px; color: var(--accent); }

/* Activity Stats */
.pc-stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.pc-stat { text-align: center; padding: 12px; background: var(--bg-secondary); border-radius: 12px; }
.pc-stat-value { font-size: 1.25rem; font-weight: 700; color: var(--text-primary); }
.pc-stat-label { font-size: 0.75rem; color: var(--text-secondary); margin-top: 4px; }

/* Time Slider */
.pc-slider-wrap { margin: 12px 0; }
.pc-slider { width: 100%; appearance: none; height: 6px; border-radius: 3px; background: var(--border); outline: none; }
.pc-slider::-webkit-slider-thumb { appearance: none; width: 24px; height: 24px; border-radius: 50%; background: var(--accent); cursor: pointer; }
.pc-slider::-moz-range-thumb { width: 24px; height: 24px; border-radius: 50%; background: var(--accent); cursor: pointer; border: none; }
.pc-slider-labels { display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-secondary); margin-top: 4px; }
.pc-slider-value { text-align: center; font-size: 0.875rem; font-weight: 600; color: var(--accent); margin-bottom: 4px; }

/* Time Pickers */
.pc-time-row { display: flex; align-items: center; gap: 12px; margin: 12px 0; }
.pc-time-input { padding: 8px 12px; border-radius: 10px; border: 1px solid var(--border); background: var(--bg-secondary); color: var(--text-primary); font-size: 0.875rem; }
.pc-time-label { font-size: 0.875rem; color: var(--text-primary); min-width: 50px; }

/* Toggle Row */
.pc-toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid var(--border); }
.pc-toggle-row:last-child { border-bottom: none; }
.pc-toggle-label { font-size: 0.875rem; color: var(--text-primary); }
.pc-toggle { position: relative; width: 48px; height: 28px; flex-shrink: 0; }
.pc-toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
.pc-toggle-track { position: absolute; inset: 0; border-radius: 14px; background: var(--border); transition: background 200ms ease; cursor: pointer; }
.pc-toggle input:checked + .pc-toggle-track { background: var(--accent); }
.pc-toggle-thumb { position: absolute; top: 2px; left: 2px; width: 24px; height: 24px; border-radius: 50%; background: white; box-shadow: 0 1px 3px rgba(0,0,0,0.2); transition: transform 200ms ease; pointer-events: none; }
.pc-toggle input:checked ~ .pc-toggle-thumb { transform: translateX(20px); }

/* Difficulty Selector */
.pc-difficulty { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
.pc-diff-btn { padding: 8px 16px; border-radius: 10px; border: 2px solid var(--border); background: transparent; cursor: pointer; font-size: 0.85rem; color: var(--text-primary); transition: all 150ms ease; }
.pc-diff-btn:hover { border-color: var(--accent); }
.pc-diff-btn.active { border-color: var(--accent); background: rgba(16, 185, 129, 0.1); font-weight: 600; }

/* Approval Queue */
.pc-approval-item { display: flex; align-items: center; justify-content: space-between; padding: 12px; background: var(--bg-secondary); border-radius: 12px; margin-bottom: 8px; }
.pc-approval-info { flex: 1; }
.pc-approval-action { font-size: 0.875rem; font-weight: 600; color: var(--text-primary); }
.pc-approval-detail { font-size: 0.75rem; color: var(--text-secondary); margin-top: 2px; }
.pc-approval-btns { display: flex; gap: 8px; }

/* Buttons */
.pc-btn { padding: 10px 20px; border-radius: 12px; border: none; font-size: 0.875rem; font-weight: 600; cursor: pointer; transition: all 150ms ease; }
.pc-btn-primary { background: var(--accent); color: white; }
.pc-btn-primary:hover { opacity: 0.9; }
.pc-btn-sm { padding: 6px 14px; font-size: 0.8rem; border-radius: 8px; }
.pc-btn-success { background: #10B981; color: white; }
.pc-btn-danger { background: transparent; border: 2px solid #EF4444; color: #EF4444; }
.pc-btn-danger:hover { background: #EF4444; color: white; }
.pc-btn-outline { background: transparent; border: 2px solid var(--border); color: var(--text-primary); }
.pc-btn-outline:hover { border-color: var(--accent); color: var(--accent); }
.pc-btn-warning { background: #F59E0B; color: white; }
.pc-btn-pause { width: 100%; margin-top: 12px; }

/* PIN Section */
.pc-pin-input { display: flex; gap: 8px; justify-content: center; margin: 16px 0; }
.pc-pin-digit { width: 48px; height: 56px; text-align: center; font-size: 1.5rem; font-weight: 700; border: 2px solid var(--border); border-radius: 12px; background: var(--bg-secondary); color: var(--text-primary); }
.pc-pin-digit:focus { border-color: var(--accent); outline: none; }
.pc-pin-error { color: #EF4444; font-size: 0.8rem; text-align: center; margin-top: 8px; }
.pc-pin-locked { text-align: center; padding: 20px; color: var(--text-secondary); font-size: 0.875rem; }

/* Empty State */
.pc-empty { text-align: center; padding: 32px 16px; color: var(--text-secondary); }
.pc-empty svg { width: 48px; height: 48px; margin: 0 auto 12px; opacity: 0.5; }
.pc-empty-text { font-size: 0.875rem; }

/* Independence */
.pc-independence { text-align: center; padding: 16px; margin-top: 12px; }
.pc-independence p { font-size: 0.875rem; color: var(--text-secondary); margin-bottom: 12px; }
`;

// ---------- Module ----------

export default class ParentalDashboard {
  private _container: HTMLElement | null = null;
  private _ctx: AweniContext | null = null;
  private _service: ParentalControlService | null = null;
  private _selectedChild: ChildProfile | null = null;
  private _children: ChildProfile[] = [];
  private _activity: ChildActivity | null = null;
  private _approvals: ApprovalItem[] = [];
  private _controls: ParentalControls | null = null;
  private _pinMode: 'verify' | 'set' | 'change' | null = null;
  private _pinVerified = false;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this._container = container;
    this._ctx = ctx;

    // Inject styles
    if (!document.getElementById('pc-dashboard-styles')) {
      const style = document.createElement('style');
      style.id = 'pc-dashboard-styles';
      style.textContent = DASHBOARD_CSS;
      document.head.appendChild(style);
    }

    // Init service
    this._service = getParentalControlService() || initParentalControlService(
      ctx.apiBase,
      () => ctx.auth.getToken(),
    );

    // Load children from profiles
    this._children = (ctx.auth.profiles || [])
      .filter((p) => p.type === 'child')
      .map((p) => ({ id: p.id, name: p.name, avatar_url: p.avatar_url }));

    if (this._children.length > 0) {
      this._selectedChild = this._children[0];
    }

    // If parent has a PIN set, require verification first
    if (this._service.controls.pin_hash && !this._pinVerified) {
      this._renderPinGate();
      return;
    }

    this._pinVerified = true;
    await this._loadChildData();
    this._render();
  }

  unmount(): void {
    if (this._container) this._container.innerHTML = '';
    this._container = null;
    this._ctx = null;
  }

  // ---------- PIN Gate ----------

  private _renderPinGate(): void {
    if (!this._container || !this._ctx) return;
    const t = this._ctx.i18n.t.bind(this._ctx.i18n);

    const locked = this._service?.isPinLocked();
    const lockSeconds = this._service?.getPinLockRemainingSeconds() || 0;

    this._container.innerHTML = `
      <div class="pc-dashboard">
        <h1>${t('parental_dashboard')}</h1>
        <div class="pc-card" style="text-align:center;padding:32px 20px;">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin:0 auto 16px;">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <p style="font-size:0.95rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">${t('parental_pin_required')}</p>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:16px;">${t('parental_pin_enter')}</p>
          ${locked
            ? `<div class="pc-pin-locked">${t('parental_pin_locked').replace('{seconds}', String(lockSeconds))}</div>`
            : `<div class="pc-pin-input" id="pc-pin-inputs"></div>
               <div class="pc-pin-error" id="pc-pin-error" style="display:none;"></div>`
          }
        </div>
      </div>
    `;

    if (!locked) {
      this._setupPinInputs(4);
    }
  }

  private _setupPinInputs(length: number): void {
    const wrap = this._container?.querySelector('#pc-pin-inputs');
    if (!wrap) return;

    const inputs: HTMLInputElement[] = [];
    for (let i = 0; i < length; i++) {
      const inp = document.createElement('input');
      inp.type = 'password';
      inp.inputMode = 'numeric';
      inp.maxLength = 1;
      inp.className = 'pc-pin-digit';
      inp.autocomplete = 'off';

      inp.addEventListener('input', () => {
        if (inp.value.length === 1 && i < length - 1) {
          inputs[i + 1].focus();
        }
        // Check if all filled
        const pin = inputs.map((x) => x.value).join('');
        if (pin.length === length) {
          this._verifyPin(pin);
        }
      });

      inp.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' && !inp.value && i > 0) {
          inputs[i - 1].focus();
        }
      });

      inputs.push(inp);
      wrap.appendChild(inp);
    }

    // Focus first input
    setTimeout(() => inputs[0]?.focus(), 100);
  }

  private async _verifyPin(pin: string): Promise<void> {
    if (!this._service) return;

    const valid = this._service.verifyPin(pin);
    if (valid) {
      this._pinVerified = true;
      await this._loadChildData();
      this._render();
    } else {
      const errEl = this._container?.querySelector('#pc-pin-error') as HTMLElement;
      if (errEl) {
        errEl.style.display = '';
        errEl.textContent = this._ctx?.i18n.t('parental_pin_wrong') || 'Incorrect PIN';
      }
      // Clear inputs
      const inputs = this._container?.querySelectorAll('.pc-pin-digit') as NodeListOf<HTMLInputElement>;
      inputs?.forEach((inp) => { inp.value = ''; });
      inputs?.[0]?.focus();

      // Check if now locked
      if (this._service.isPinLocked()) {
        this._renderPinGate();
      }
    }
  }

  // ---------- Data Loading ----------

  private async _loadChildData(): Promise<void> {
    if (!this._selectedChild || !this._ctx) return;

    const token = this._ctx.auth.getToken();
    if (!token) return;

    // Load activity and controls for selected child
    try {
      const [actRes, ctrlRes, approvalRes] = await Promise.all([
        fetch(`${this._ctx.apiBase}/preferences?key=child_activity_summary&profile_id=${this._selectedChild.id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        }),
        fetch(`${this._ctx.apiBase}/preferences?key=parental_controls&profile_id=${this._selectedChild.id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        }),
        fetch(`${this._ctx.apiBase}/preferences?key=approval_queue&profile_id=${this._selectedChild.id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        }),
      ]);

      if (actRes.ok) {
        const data = await actRes.json();
        this._activity = data?.value || this._getDefaultActivity();
      } else {
        this._activity = this._getDefaultActivity();
      }

      if (ctrlRes.ok) {
        const data = await ctrlRes.json();
        this._controls = data?.value || this._service?.controls || null;
      } else {
        this._controls = this._service?.controls || null;
      }

      if (approvalRes.ok) {
        const data = await approvalRes.json();
        this._approvals = data?.value || [];
      }
    } catch {
      this._activity = this._getDefaultActivity();
      this._controls = this._service?.controls || null;
      this._approvals = [];
    }
  }

  private _getDefaultActivity(): ChildActivity {
    return {
      screenTimeToday: 0,
      screenTimeWeek: 0,
      gamesPlayed: [],
      cardsReviewed: 0,
      articlesRead: 0,
      lastActiveTime: null,
    };
  }

  // ---------- Main Render ----------

  private _render(): void {
    if (!this._container || !this._ctx) return;
    const t = this._ctx.i18n.t.bind(this._ctx.i18n);
    const act = this._activity || this._getDefaultActivity();
    const ctrl = this._controls || this._service?.controls;

    this._container.innerHTML = `
      <div class="pc-dashboard">
        <h1>${t('parental_dashboard')}</h1>
        <p class="pc-subtitle">${t('parental_dashboard_sub')}</p>

        ${this._renderChildTabs(t)}
        ${this._children.length === 0
          ? this._renderNoChildren(t)
          : `
            ${this._renderActivitySummary(t, act)}
            ${this._renderTimeControls(t, ctrl)}
            ${this._renderContentControls(t, ctrl)}
            ${this._renderApprovalQueue(t)}
            ${this._renderPinManagement(t)}
            ${this._renderIndependence(t)}
          `
        }
      </div>
    `;

    this._bindEvents();
  }

  // ---------- Sub-Renders ----------

  private _renderChildTabs(t: (k: string) => string): string {
    if (this._children.length === 0) return '';

    return `
      <div class="pc-tabs">
        ${this._children.map((child) => `
          <div class="pc-tab ${child.id === this._selectedChild?.id ? 'active' : ''}" data-child-id="${child.id}">
            <div class="pc-tab-avatar">
              ${child.avatar_url
                ? `<img src="${child.avatar_url}" alt="${child.name}">`
                : child.name.charAt(0).toUpperCase()
              }
            </div>
            <span class="pc-tab-name">${this._esc(child.name)}</span>
          </div>
        `).join('')}
      </div>
    `;
  }

  private _renderNoChildren(t: (k: string) => string): string {
    return `
      <div class="pc-empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
          <line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/>
        </svg>
        <p class="pc-empty-text">${t('parental_no_children')}</p>
      </div>
    `;
  }

  private _renderActivitySummary(t: (k: string) => string, act: ChildActivity): string {
    return `
      <div class="pc-card">
        <div class="pc-card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
          ${t('parental_activity')}
        </div>
        <div class="pc-stats-grid">
          <div class="pc-stat">
            <div class="pc-stat-value">${act.screenTimeToday}<span style="font-size:0.75rem;font-weight:400;"> min</span></div>
            <div class="pc-stat-label">${t('parental_screen_time_today')}</div>
          </div>
          <div class="pc-stat">
            <div class="pc-stat-value">${Math.round(act.screenTimeWeek / 60)}<span style="font-size:0.75rem;font-weight:400;"> h</span></div>
            <div class="pc-stat-label">${t('parental_screen_time_week')}</div>
          </div>
          <div class="pc-stat">
            <div class="pc-stat-value">${act.gamesPlayed.reduce((sum, g) => sum + g.count, 0)}</div>
            <div class="pc-stat-label">${t('parental_games_played')}</div>
          </div>
          <div class="pc-stat">
            <div class="pc-stat-value">${act.cardsReviewed}</div>
            <div class="pc-stat-label">${t('parental_cards_reviewed')}</div>
          </div>
        </div>
        ${act.lastActiveTime ? `
          <p style="font-size:0.75rem;color:var(--text-secondary);text-align:center;margin-top:12px;">
            ${t('parental_last_active')}: ${new Date(act.lastActiveTime).toLocaleString()}
          </p>
        ` : ''}
      </div>
    `;
  }

  private _renderTimeControls(t: (k: string) => string, ctrl?: ParentalControls | null): string {
    const limit = ctrl?.daily_time_limit_min ?? 0;
    const sliderIndex = TIME_LIMIT_OPTIONS.findIndex((o) => o.value === limit);
    const idx = sliderIndex >= 0 ? sliderIndex : TIME_LIMIT_OPTIONS.length - 1;
    const displayLabel = limit === 0 ? t('parental_unlimited') : TIME_LIMIT_OPTIONS[idx]?.label || `${limit} min`;

    return `
      <div class="pc-card">
        <div class="pc-card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          ${t('parental_time_controls')}
        </div>

        <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">${t('parental_daily_limit')}</p>
        <div class="pc-slider-wrap">
          <div class="pc-slider-value" id="pc-time-value">${displayLabel}</div>
          <input type="range" class="pc-slider" id="pc-time-slider" min="0" max="${TIME_LIMIT_OPTIONS.length - 1}" value="${idx}" step="1">
          <div class="pc-slider-labels">
            <span>30m</span><span>1h</span><span>1.5h</span><span>2h</span><span>3h</span><span>${t('parental_unlimited')}</span>
          </div>
        </div>

        <p style="font-size:0.8rem;color:var(--text-secondary);margin:16px 0 8px;">${t('parental_allowed_hours')}</p>
        <div class="pc-time-row">
          <span class="pc-time-label">${t('parental_from')}</span>
          <input type="time" class="pc-time-input" id="pc-hour-start" value="${String(ctrl?.allowed_hours_start ?? 7).padStart(2, '0')}:00">
          <span class="pc-time-label">${t('parental_to')}</span>
          <input type="time" class="pc-time-input" id="pc-hour-end" value="${String(ctrl?.allowed_hours_end ?? 21).padStart(2, '0')}:00">
        </div>

        <button class="pc-btn pc-btn-warning pc-btn-pause" id="pc-pause-btn">
          ${ctrl?.paused ? t('parental_resume') : t('parental_pause_now')}
        </button>
      </div>
    `;
  }

  private _renderContentControls(t: (k: string) => string, ctrl?: ParentalControls | null): string {
    const blocked = ctrl?.blocked_modules || [];
    const cap = ctrl?.difficulty_cap || null;

    return `
      <div class="pc-card">
        <div class="pc-card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          ${t('parental_content_controls')}
        </div>

        <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">${t('parental_module_access')}</p>
        ${MODULE_IDS.map((mod) => `
          <div class="pc-toggle-row">
            <span class="pc-toggle-label">${t('nav_' + (mod === 'memoriz' ? 'quran' : mod === 'balancetab' ? 'money' : mod))}</span>
            <label class="pc-toggle">
              <input type="checkbox" data-module-toggle="${mod}" ${!blocked.includes(mod) ? 'checked' : ''}>
              <span class="pc-toggle-track"></span>
              <span class="pc-toggle-thumb"></span>
            </label>
          </div>
        `).join('')}

        <p style="font-size:0.8rem;color:var(--text-secondary);margin:16px 0 8px;">${t('parental_difficulty_cap')}</p>
        <div class="pc-difficulty">
          ${DIFFICULTY_OPTIONS.map((d) => `
            <button class="pc-diff-btn ${cap === d.value ? 'active' : ''}" data-difficulty="${d.value}">
              ${t('parental_diff_' + d.key)}
            </button>
          `).join('')}
          <button class="pc-diff-btn ${cap === null ? 'active' : ''}" data-difficulty="none">
            ${t('parental_no_cap')}
          </button>
        </div>
      </div>
    `;
  }

  private _renderApprovalQueue(t: (k: string) => string): string {
    if (this._approvals.length === 0) {
      return `
        <div class="pc-card">
          <div class="pc-card-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
            ${t('parental_approvals')}
          </div>
          <p style="font-size:0.85rem;color:var(--text-secondary);text-align:center;padding:8px 0;">
            ${t('parental_no_approvals')}
          </p>
        </div>
      `;
    }

    return `
      <div class="pc-card">
        <div class="pc-card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
          ${t('parental_approvals')} (${this._approvals.length})
        </div>
        ${this._approvals.map((item) => `
          <div class="pc-approval-item">
            <div class="pc-approval-info">
              <div class="pc-approval-action">${this._esc(item.action)}</div>
              <div class="pc-approval-detail">${this._esc(item.detail)}</div>
            </div>
            <div class="pc-approval-btns">
              <button class="pc-btn pc-btn-sm pc-btn-success" data-approve="${item.id}">${t('parental_approve')}</button>
              <button class="pc-btn pc-btn-sm pc-btn-danger" data-deny="${item.id}">${t('parental_deny')}</button>
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  private _renderPinManagement(t: (k: string) => string): string {
    const hasPin = !!this._service?.controls.pin_hash;

    return `
      <div class="pc-card">
        <div class="pc-card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          ${t('parental_pin')}
        </div>
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:12px;">
          ${hasPin ? t('parental_pin_set') : t('parental_pin_not_set')}
        </p>
        <button class="pc-btn pc-btn-outline" id="pc-pin-change-btn">
          ${hasPin ? t('parental_pin_change') : t('parental_pin_setup')}
        </button>
      </div>
    `;
  }

  private _renderIndependence(t: (k: string) => string): string {
    if (!this._selectedChild) return '';

    return `
      <div class="pc-card">
        <div class="pc-card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          ${t('parental_independence')}
        </div>
        <div class="pc-independence">
          <p>${t('parental_independence_desc').replace('{name}', this._esc(this._selectedChild.name))}</p>
          <button class="pc-btn pc-btn-danger" id="pc-independence-btn">
            ${t('parental_grant_independence')}
          </button>
        </div>
      </div>
    `;
  }

  // ---------- Event Binding ----------

  private _bindEvents(): void {
    if (!this._container) return;

    // Child tab selection
    this._container.querySelectorAll('.pc-tab').forEach((tab) => {
      tab.addEventListener('click', async () => {
        const id = Number((tab as HTMLElement).dataset.childId);
        this._selectedChild = this._children.find((c) => c.id === id) || null;
        await this._loadChildData();
        this._render();
      });
    });

    // Time slider
    const slider = this._container.querySelector('#pc-time-slider') as HTMLInputElement;
    const valueDisplay = this._container.querySelector('#pc-time-value') as HTMLElement;
    slider?.addEventListener('input', () => {
      const idx = Number(slider.value);
      const opt = TIME_LIMIT_OPTIONS[idx];
      const t = this._ctx!.i18n.t.bind(this._ctx!.i18n);
      valueDisplay.textContent = opt.value === 0 ? t('parental_unlimited') : opt.label;
    });
    slider?.addEventListener('change', () => {
      const idx = Number(slider.value);
      const opt = TIME_LIMIT_OPTIONS[idx];
      this._saveControl('daily_time_limit_min', opt.value === 0 ? null : opt.value);
    });

    // Time pickers
    const startInput = this._container.querySelector('#pc-hour-start') as HTMLInputElement;
    const endInput = this._container.querySelector('#pc-hour-end') as HTMLInputElement;
    startInput?.addEventListener('change', () => {
      this._saveControl('allowed_hours_start', parseInt(startInput.value.split(':')[0], 10));
    });
    endInput?.addEventListener('change', () => {
      this._saveControl('allowed_hours_end', parseInt(endInput.value.split(':')[0], 10));
    });

    // Pause button
    this._container.querySelector('#pc-pause-btn')?.addEventListener('click', () => {
      const current = this._controls?.paused ?? false;
      this._saveControl('paused', !current);
    });

    // Module toggles
    this._container.querySelectorAll('[data-module-toggle]').forEach((input) => {
      input.addEventListener('change', () => {
        const mod = (input as HTMLInputElement).dataset.moduleToggle!;
        const checked = (input as HTMLInputElement).checked;
        const blocked = [...(this._controls?.blocked_modules || [])];
        if (checked) {
          const idx = blocked.indexOf(mod);
          if (idx >= 0) blocked.splice(idx, 1);
        } else {
          if (!blocked.includes(mod)) blocked.push(mod);
        }
        this._saveControl('blocked_modules', blocked);
      });
    });

    // Difficulty buttons
    this._container.querySelectorAll('[data-difficulty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const val = (btn as HTMLElement).dataset.difficulty!;
        const cap = val === 'none' ? null : val;
        this._saveControl('difficulty_cap', cap);
      });
    });

    // Approval buttons
    this._container.querySelectorAll('[data-approve]').forEach((btn) => {
      btn.addEventListener('click', () => this._handleApproval((btn as HTMLElement).dataset.approve!, true));
    });
    this._container.querySelectorAll('[data-deny]').forEach((btn) => {
      btn.addEventListener('click', () => this._handleApproval((btn as HTMLElement).dataset.deny!, false));
    });

    // PIN change
    this._container.querySelector('#pc-pin-change-btn')?.addEventListener('click', () => {
      this._showPinSetupDialog();
    });

    // Independence
    this._container.querySelector('#pc-independence-btn')?.addEventListener('click', () => {
      this._handleIndependence();
    });
  }

  // ---------- Actions ----------

  private async _saveControl(key: string, value: unknown): Promise<void> {
    if (!this._service || !this._controls) return;
    (this._controls as unknown as Record<string, unknown>)[key] = value;
    await this._service.saveControls({ [key]: value } as Partial<ParentalControls>);
    this._render();
  }

  private async _handleApproval(id: string, approved: boolean): Promise<void> {
    if (!this._ctx) return;
    const token = this._ctx.auth.getToken();
    if (!token) return;

    try {
      await fetch(`${this._ctx.apiBase}/preferences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          key: 'approval_response',
          value: { id, approved, respondedAt: new Date().toISOString() },
        }),
      });

      this._approvals = this._approvals.filter((a) => a.id !== id);
      this._render();
    } catch { /* Network error -- silent */ }
  }

  private _showPinSetupDialog(): void {
    if (!this._container || !this._ctx) return;
    const t = this._ctx.i18n.t.bind(this._ctx.i18n);

    // Simple inline dialog
    const dialog = document.createElement('div');
    dialog.style.cssText = 'position:fixed;inset:0;z-index:100;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.5);';
    dialog.innerHTML = `
      <div style="background:var(--card-bg);border-radius:20px;padding:24px;max-width:320px;width:90%;text-align:center;">
        <h3 style="font-size:1.1rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">${t('parental_pin_setup')}</h3>
        <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:16px;">${t('parental_pin_setup_desc')}</p>
        <div class="pc-pin-input" id="pc-new-pin-inputs"></div>
        <div style="display:flex;gap:8px;margin-top:16px;justify-content:center;">
          <button class="pc-btn pc-btn-outline" id="pc-pin-cancel">${t('common_cancel')}</button>
          <button class="pc-btn pc-btn-primary" id="pc-pin-confirm" disabled>${t('common_save')}</button>
        </div>
      </div>
    `;

    document.body.appendChild(dialog);

    // Setup inputs
    const wrap = dialog.querySelector('#pc-new-pin-inputs')!;
    const inputs: HTMLInputElement[] = [];
    let pinValue = '';

    for (let i = 0; i < 4; i++) {
      const inp = document.createElement('input');
      inp.type = 'password';
      inp.inputMode = 'numeric';
      inp.maxLength = 1;
      inp.className = 'pc-pin-digit';
      inp.autocomplete = 'off';

      inp.addEventListener('input', () => {
        if (inp.value.length === 1 && i < 3) inputs[i + 1].focus();
        pinValue = inputs.map((x) => x.value).join('');
        const confirmBtn = dialog.querySelector('#pc-pin-confirm') as HTMLButtonElement;
        if (confirmBtn) confirmBtn.disabled = pinValue.length < 4;
      });

      inp.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' && !inp.value && i > 0) inputs[i - 1].focus();
      });

      inputs.push(inp);
      wrap.appendChild(inp);
    }

    setTimeout(() => inputs[0]?.focus(), 100);

    // Cancel
    dialog.querySelector('#pc-pin-cancel')?.addEventListener('click', () => dialog.remove());
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.remove(); });

    // Confirm
    dialog.querySelector('#pc-pin-confirm')?.addEventListener('click', async () => {
      if (pinValue.length >= 4 && this._service) {
        // Store simple hash
        let hash = 0;
        for (let i = 0; i < pinValue.length; i++) {
          hash = ((hash << 5) - hash) + pinValue.charCodeAt(i);
          hash = hash & hash;
        }
        const pinHash = 'pin_' + Math.abs(hash).toString(36);
        await this._service.saveControls({ pin_hash: pinHash });
        dialog.remove();
        this._render();
      }
    });
  }

  private _handleIndependence(): void {
    if (!this._selectedChild || !this._ctx) return;
    const t = this._ctx.i18n.t.bind(this._ctx.i18n);

    const confirmed = confirm(
      t('parental_independence_confirm').replace('{name}', this._selectedChild.name)
    );

    if (confirmed && this._service) {
      this._service.saveControls({ enabled: false });
      this._render();
    }
  }

  // ---------- Helpers ----------

  private _esc(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
